import { InfoIcon, ZoomIn, ZoomOut } from 'lucide-react';

import { Button } from '../../ui/catalyst/button';
import { Switch } from '../../ui/catalyst/switch';

interface TimelineControlsProps {
  zoomLevel: number;
  onZoomChange: (level: number) => void;
  minZoom: number;
  maxZoom: number;
  showGaps: boolean;
  onToggleGaps: (show: boolean) => void;
  gapThresholdDays: number;
  onGapThresholdChange: (days: number) => void;
  encounterCount: number;
  providerCount: number;
}

export const TimelineControls = ({
  zoomLevel,
  onZoomChange,
  minZoom,
  maxZoom,
  showGaps,
  onToggleGaps,
  gapThresholdDays,
  onGapThresholdChange,
  encounterCount,
  providerCount,
}: TimelineControlsProps) => {
  const handleZoomIn = () => {
    onZoomChange(Math.min(zoomLevel + (maxZoom - minZoom) / 10, maxZoom));
  };

  const handleZoomOut = () => {
    onZoomChange(Math.max(zoomLevel - (maxZoom - minZoom) / 10, minZoom));
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onZoomChange(Number(e.target.value));
  };

  const handleGapThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onGapThresholdChange(Number(e.target.value));
  };

  // Calculate zoom percentage for display
  const zoomPercentage = Math.round(
    ((zoomLevel - minZoom) / (maxZoom - minZoom)) * 100,
  );

  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-50 border-b border-gray-200">
      <div className="flex items-center space-x-3 text-xs text-gray-600">
        <div className="flex items-center">
          <span className="inline-flex items-center mr-1.5">
            <InfoIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
            Events:
          </span>
          <span className="font-medium text-gray-700">{encounterCount}</span>
        </div>
        <div className="w-px h-3 bg-gray-300" />
        <div className="flex items-center">
          <span className="inline-flex items-center mr-1.5">
            <InfoIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
            Providers:
          </span>
          <span className="font-medium text-gray-700">{providerCount}</span>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-1.5">
          <Button
            plain
            onClick={handleZoomOut}
            aria-label="Zoom out"
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
            disabled={zoomLevel === minZoom}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>

          <div className="flex flex-col items-center">
            <input
              type="range"
              min={minZoom}
              max={maxZoom}
              step="1" // Smoother zoom
              value={zoomLevel}
              onChange={handleSliderChange}
              className="w-28 h-2 appearance-none bg-gray-300 rounded-full outline-none cursor-pointer accent-blue-600"
              aria-label="Timeline zoom"
            />
            <div className="text-[10px] text-gray-500 mt-0.5">
              Zoom: {zoomPercentage}%
            </div>
          </div>

          <Button
            plain
            onClick={handleZoomIn}
            aria-label="Zoom in"
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
            disabled={zoomLevel === maxZoom}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <div className="h-4 w-px bg-gray-300" />

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">Highlight Gaps</span>
          <Switch
            checked={showGaps}
            onChange={onToggleGaps}
            aria-label="Toggle treatment gap highlighting"
          />
        </div>

        {showGaps && (
          <div className="flex items-center space-x-1.5">
            <span className="text-xs text-gray-600 whitespace-nowrap">
              Min. {gapThresholdDays}d gap
            </span>
            <input
              type="range"
              min="7"
              max="90"
              step="1"
              value={gapThresholdDays}
              onChange={handleGapThresholdChange}
              className="w-20 h-1.5 appearance-none bg-gray-300 rounded-full outline-none cursor-pointer accent-blue-600"
              aria-label="Gap threshold in days"
            />
          </div>
        )}
      </div>
    </div>
  );
};
